import { ref, computed, useContext } from '@nuxtjs/composition-api'

const catalogData = ref(null)

/** Build a flat map of plan code -> plan (only plans with code) from catalog array of partners. */
function buildPlanMap (catalog) {
  const map = new Map()
  if (!Array.isArray(catalog)) {
    return map
  }
  catalog.forEach(partner => {
    if (!partner?.plans) {
      return
    }
    partner.plans.forEach(plan => {
      if (plan?.code) {
        map.set(plan.code, { ...plan, partnerProduct: partner.product })
      }
    })
  })
  return map
}

/** Build a flat map of addon code -> addon from catalog array of partners. */
function buildAddonMap (catalog) {
  const map = new Map()
  if (!Array.isArray(catalog)) {
    return map
  }
  catalog.forEach((partner) => {
    const partnerCode = partner?.code
    const partnerAddons = partner?.planAddons
    if (!partnerCode || !Array.isArray(partnerAddons)) {
      return
    }
    partnerAddons.forEach((addon) => {
      const entry = typeof addon === 'string' ? { code: addon } : addon
      if (entry?.code) {
        map.set(entry.code, { ...entry, partnerCode })
      }
    })
  })
  return map
}

function normalizeCatalogPayload (raw) {
  const payload = [raw, raw?.responseObject, raw?.data].find(Array.isArray)
  return payload || []
}

export default function useProductConfigurationCatalog (appContext) {
  const app = appContext ?? useContext().app
  const hubbleClient = app.$hubble

  async function fetchCatalogList () {
    try {
      const res = await hubbleClient.playMix.getProductConfigurationProductCatalog()
      return normalizeCatalogPayload(res?.data)
    } catch (e) {
      console.warn('useProductConfigurationCatalog: failed to load catalog', e)
      return []
    }
  }

  /**
   * Load product configuration catalog from backend API.
   * Uses playMix.getProductConfigurationProductCatalog (fetch-play-partners-plans).
   */
  async function loadProductConfigurationCatalog () {
    if (catalogData.value) {
      return catalogData.value
    }
    catalogData.value = await fetchCatalogList()
    return catalogData.value
  }

  const planMap = computed(() => buildPlanMap(catalogData.value))
  const addonMap = computed(() => buildAddonMap(catalogData.value))

  /**
   * From mix product codes (e.g. from PlayPoList), return plans that have price > 0.
   * Used to show extra lines in cart for paid plans. Ignore partners (icons only).
   */
  function getPaidPlansFromMixProducts (mixProductCodes) {
    if (!mixProductCodes || !Array.isArray(mixProductCodes)) {
      return []
    }
    const map = planMap.value
    return mixProductCodes
      .map(code => (typeof code === 'string' ? code.trim() : code))
      .filter(Boolean)
      .map(code => map.get(code))
      .filter(plan => plan && Number(plan.price) > 0)
  }

  function getPaidAddonsFromCodes (addonCodes) {
    const map = addonMap.value
    if (!addonCodes || !Array.isArray(addonCodes)) {
      return []
    }
    const unique = [...new Set(addonCodes.map(code => (typeof code === 'string' ? code.trim() : '')).filter(Boolean))]
    return unique
      .map(code => map.get(code))
      .filter(addon => addon && Number(addon.price) > 0)
  }

  function buildTilkobDisplayItems (mixProductCodes, addonCodes) {
    const paidPlans = getPaidPlansFromMixProducts(mixProductCodes)
    const paidAddons = getPaidAddonsFromCodes(addonCodes)
    const addonsByPartner = new Map()

    paidAddons.forEach((addon) => {
      const partnerCode = addon?.partnerCode
      if (!partnerCode) {
        return
      }
      const existing = addonsByPartner.get(partnerCode) || []
      existing.push(addon)
      addonsByPartner.set(partnerCode, existing)
    })

    const items = paidPlans.map((plan) => {
      const partnerCode = plan?.partnerCode ?? plan?.partner
      const addons = partnerCode ? (addonsByPartner.get(partnerCode) || []) : []
      if (partnerCode) {
        addonsByPartner.delete(partnerCode)
      }
      return { key: plan.code, plan, addons }
    })

    addonsByPartner.forEach((addons, partnerCode) => {
      if (addons?.length) {
        items.push({ key: `addons-${partnerCode}`, plan: null, addons })
      }
    })

    return items
  }

  return {
    loadProductConfigurationCatalog,
    catalogData,
    planMap,
    addonMap,
    getPaidPlansFromMixProducts,
    getPaidAddonsFromCodes,
    buildTilkobDisplayItems
  }
}
